import type { ReactNode } from 'react';
import { Button, Spinner } from '../components/ui';
import { STREAM_REASON_LABEL } from '../lib/labels';
import type { StreamStatus, ViewerState } from './socket';

export interface OverlayProps {
  state: ViewerState;
  isController: boolean;
  /** true while the customer has pressed 완료 */
  done?: boolean;
  onOpenBrowser?: () => void;
  onRequestControl?: () => void;
  requesting?: boolean;
}

const KICK_REASON: Record<string, string> = {
  profile_archived: '브라우저 프로필이 보관되어 연결이 종료되었습니다',
  support_session_revoked: '지원 세션이 폐기되어 연결이 종료되었습니다',
  replaced: '다른 창에서 같은 계정으로 접속하여 이 연결이 종료되었습니다',
};

function Panel({
  title,
  detail,
  spinner,
  action,
}: {
  title: string;
  detail?: ReactNode;
  spinner?: boolean;
  action?: ReactNode;
}) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6">
      <div className="flex max-w-md flex-col items-center gap-3 rounded-2xl bg-black/70 px-8 py-6 text-center text-white backdrop-blur-sm">
        {spinner && <Spinner className="size-6" />}
        <p className="text-base font-semibold">{title}</p>
        {detail && <p className="text-sm text-white/80">{detail}</p>}
        {action}
      </div>
    </div>
  );
}

/** Explains why the picture is missing/frozen, or shows the "read-only" pill for viewers. */
export function ViewerOverlay({
  state,
  isController,
  done,
  onOpenBrowser,
  onRequestControl,
  requesting,
}: OverlayProps) {
  const snap = state.snapshot;
  if (state.connection === 'closed') {
    const reason = state.closed?.message ?? '';
    return (
      <Panel
        title="연결이 종료되었습니다"
        detail={KICK_REASON[reason] ?? reason}
        action={
          <Button size="sm" onClick={() => window.location.reload()}>
            다시 연결
          </Button>
        }
      />
    );
  }
  if (state.connection === 'connecting') return <Panel title="연결 중…" spinner />;
  if (state.connection === 'reconnecting') return <Panel title="다시 연결하는 중…" spinner />;
  if (!snap) return <Panel title="연결 중…" spinner />;
  if (!snap.nodeOnline)
    return (
      <Panel
        title="노드가 오프라인입니다"
        detail="VOS Bridge Node가 다시 접속하면 자동으로 이어집니다"
      />
    );
  if (snap.runtime === 'launching') return <Panel title="브라우저를 시작하는 중…" spinner />;
  if (snap.runtime === 'error')
    return (
      <Panel
        title="브라우저 오류"
        detail={state.runtimeError?.message}
        action={
          state.can.openBrowser && onOpenBrowser ? (
            <Button size="sm" variant="primary" onClick={onOpenBrowser}>
              다시 열기
            </Button>
          ) : undefined
        }
      />
    );
  if (snap.runtime === 'closed')
    return (
      <Panel
        title="브라우저가 닫혀 있습니다"
        detail={state.can.openBrowser ? undefined : '운영자가 브라우저를 열면 화면이 표시됩니다'}
        action={
          state.can.openBrowser && onOpenBrowser ? (
            <Button size="sm" variant="primary" onClick={onOpenBrowser}>
              브라우저 열기
            </Button>
          ) : undefined
        }
      />
    );
  if (snap.tabs.length === 0) return <Panel title="열린 탭이 없습니다" />;
  if (!state.tabId) return <Panel title="탭을 선택하세요" />;

  const stream: StreamStatus | undefined = state.streams[state.tabId];
  if (stream && stream.state !== 'active') {
    const label =
      stream.state === 'paused'
        ? (STREAM_REASON_LABEL[stream.reason ?? ''] ?? '일시 중지되었습니다')
        : (STREAM_REASON_LABEL[stream.reason ?? ''] ?? '스트리밍이 중지되었습니다');
    return (
      <Panel
        title={label}
        detail={
          stream.reason === 'inactive_tab'
            ? isController
              ? '이 탭을 클릭하면 원격 창에서 활성화됩니다'
              : '제어 중인 참여자가 보고 있는 탭만 실시간으로 표시됩니다'
            : undefined
        }
      />
    );
  }

  if (done)
    return (
      <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center">
        <span className="rounded-full bg-success px-3 py-1 text-sm font-medium text-white shadow">
          완료되었습니다. 운영자가 이어서 작업합니다
        </span>
      </div>
    );

  if (!isController)
    return (
      <div className="absolute inset-x-0 bottom-4 flex justify-center">
        <div className="flex items-center gap-2 rounded-full bg-black/70 py-1 pl-3 pr-1 text-sm text-white shadow backdrop-blur-sm">
          열람 중
          {onRequestControl && (
            <button
              type="button"
              onClick={onRequestControl}
              disabled={requesting}
              className="rounded-full bg-white/90 px-3 py-0.5 text-xs font-medium text-ink hover:bg-white disabled:opacity-60"
            >
              {requesting ? '요청 중…' : '제어 요청'}
            </button>
          )}
        </div>
      </div>
    );
  return null;
}
